import React from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Chat as ChatIcon,
  ChevronRight as ArrowIcon,
  CheckCircle as VerifiedIcon,
  Cancel as UnverifiedIcon,
} from '@mui/icons-material';
import { Client } from '../../types/client';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { useNavigate } from 'react-router-dom';

interface ClientCardProps {
  client: Client;
  onViewDetails: (client: Client) => void;
}

export const ClientCard: React.FC<ClientCardProps> = ({ client, onViewDetails }) => {
  const navigate = useNavigate();
  const isBlocked = client.connectionStatus === 'pending' || client.connectionStatus === 'rejected';

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isBlocked) {
      navigate(`/client/${client.id}`);
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'row',
        borderRadius: 3,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: 'none',
        position: 'relative', // Just in case
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          transform: 'translateY(-1px)',
          borderColor: 'primary.light'
        }
      }}
    >
      {/* Left Section: Main Info - Dynamic width */}
      <Box 
        onClick={() => onViewDetails(client)}
        sx={{ 
          flex: 1, 
          p: 1.5, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          minWidth: 0, // Essential for noWrap text to work
          overflow: 'hidden'
        }}
      >
        <UserAvatar 
          path={client.avatar} 
          name={client.name} 
          sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography 
              variant="body1" 
              fontWeight={700} 
              noWrap 
              sx={{ 
                lineHeight: 1.2,
                fontSize: '0.9rem'
              }}
            >
              {client.name} {client.surname}
            </Typography>
            {client.isEmailVerified ? (
              <VerifiedIcon color="success" sx={{ fontSize: 14, flexShrink: 0 }} />
            ) : (
              <UnverifiedIcon color="action" sx={{ fontSize: 14, opacity: 0.5, flexShrink: 0 }} />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {client.groups && client.groups.length > 0 ? (
              client.groups.slice(0, 2).map(g => (
                <Chip
                  key={g.id}
                  label={g.name}
                  size="small"
                  sx={{
                    bgcolor: g.color,
                    color: '#fff',
                    fontSize: '8px',
                    height: '14px',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', opacity: 0.7 }}>
                Без групи
              </Typography>
            )}
            {client.groups && client.groups.length > 2 && (
              <Typography variant="caption" sx={{ fontSize: '9px' }}>
                +{client.groups.length - 2}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right Section: Action Cluster - Fixed to the right */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'stretch', 
          flexShrink: 0,
          ml: 'auto', // Push to the absolute right
          borderLeft: '1px solid rgba(0,0,0,0.06)'
        }}
      >
        {/* Chat Action */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            px: 0.75
          }}
        >
          <Tooltip title={isBlocked ? 'Чат очікує підтвердження' : 'Чат'}>
            <span>
              <IconButton 
                size="small" 
                color="primary"
                disabled={isBlocked}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/chat?clientId=${client.id}`);
                }}
                sx={{ 
                  width: 34,
                  height: 34,
                  bgcolor: alpha('#1976d2', 0.04),
                  '&:hover': { bgcolor: alpha('#1976d2', 0.1) }
                }}
              >
                <ChatIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Navigation Action */}
        <Box 
          onClick={handleNavigate}
          sx={{ 
            width: 44,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderLeft: '1px solid rgba(0,0,0,0.04)',
            cursor: isBlocked ? 'default' : 'pointer',
            transition: 'all 0.2s',
            bgcolor: isBlocked ? 'rgba(0,0,0,0.02)' : 'transparent',
            '&:hover': {
              bgcolor: isBlocked ? 'transparent' : alpha('#1976d2', 0.05),
            }
          }}
        >
          <ArrowIcon 
            sx={{ 
              fontSize: 24, 
              color: isBlocked ? 'text.disabled' : 'primary.main',
              transition: 'transform 0.2s',
              '.MuiBox-root:hover &': {
                transform: isBlocked ? 'none' : 'translateX(3px)'
              }
            }} 
          />
        </Box>
      </Box>
    </Card>
  );
};
